import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Image as RNImage,
  ActivityIndicator,
  Switch,
  DeviceEventEmitter,
  Platform,
} from "react-native";
import {Image,Video as VideoIcon} from "../../../assets/icons";
import Avatar from "../../Components/Avatar";
import Button from "../../Components/Button";
import Header from "../../Components/Header";
import Input from "../../Components/Input";
import ScreenWrapper from "../../Components/ScreenWrapper";
import { theme } from "../../constants/theme";
import { useAuth } from "../../Utils/AuthProvider";
import { hp, wp } from "../../helpers/common";
import {
  getSupabaseFileUrl,
  uploadFile
} from "../../services/imageService";
import { createOrUpdatePost } from "../../services/postService";
import { generateVideoThumbnail } from "../../Utils/videoThumbnails";
import { useNavigation, useRoute } from "@react-navigation/native";
import { launchImageLibrary, ImageLibraryOptions, Asset } from 'react-native-image-picker';
import { PermissionManager } from '../../Utils/permissions';
import VideoPlayer from 'react-native-video';
import { fetchCommunityById } from '../../services/communityService';
import {
  validateFileSize,
  showFileSizeError,
  showFileSizeInfo,
  MAX_FILE_SIZE,
  formatFileSize,
} from '../../Utils/fileValidation';
import { compressVideo, compressImage } from '../../services/compressionService';

// Define types
interface PostParams {
  id?: string;
  body?: string;
  file?: string | string[];
  is_deal?: boolean;
}

interface RouteParams {
  id?: string;
  body?: string;
  file?: string | string[];
  communityId?: string;
  is_deal?: boolean;
}

interface FileAsset {
  uri: string;
  type: string;
  fileName?: string;
  thumbnailUri?: string; // For video thumbnails
  fileSize?: number; // Store file size for validation (bytes)
}

const MAX_IMAGES = 3;

const NewPost = () => {
  const { userData } = useAuth();
  const navigation = useNavigation();
  const route = useRoute();
  const post = (route.params && (route.params as RouteParams).id) ? (route.params as RouteParams) : null;
  const communityId = (route.params as RouteParams)?.communityId;

  // const videoRef = useRef(null);
  // Initialize files array - handle both single file (string) and array
  const initializeFiles = () => {
    if (post && post.file) {
      // Check if file is an array (multiple images)
      if (Array.isArray(post.file)) {
        return post.file.map((filePath: string) => ({
          uri: filePath,
          type: (filePath.includes('postVideos') || filePath.endsWith('.mp4') || filePath.endsWith('.mov')) ? 'video/mp4' : 'image/jpeg',
          fileName: filePath.split('/').pop() || undefined,
        }));
      } else if (typeof post.file === 'string') {
        // Single file (could be image or video)
        const isVideo = post.file.includes('postVideos') || post.file.endsWith('.mp4') || post.file.endsWith('.mov');
        return [{
          uri: post.file,
          type: isVideo ? 'video/mp4' : 'image/jpeg',
          fileName: post.file.split('/').pop() || undefined,
        }];
      }
    }
    return [];
  };

  const [files, setFiles] = useState<FileAsset[]>(initializeFiles());
  const [isDeal, setIsDeal] = useState<boolean>(post?.is_deal ?? false);
  const [body, setBody] = useState(post && post.body ? post.body : "");
  const [loading, setLoading] = useState(false);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const editorRef = useRef(null);
  const [videoPaused, setVideoPaused] = useState(true);
  const [communityName, setCommunityName] = useState<string | null>(null);

  useEffect(() => {
    if (post && post.id) {
      setBody(post.body || "");
      setFiles(initializeFiles());
      setIsDeal(post?.is_deal ?? false);
    }
    if (communityId) {
      fetchCommunityById(communityId).then(res => {
        if (res.success && res.data) setCommunityName(res.data.name);
      });
    }
  }, [post, communityId]);


  const onPick = async () => {
    try {
      // Request media permissions before launching picker
      const permissionResult = await PermissionManager.requestMediaPermissions();
      if (!permissionResult.granted) {
        Alert.alert('Permission Required', 'Gallery access is required to select media files.');
        return;
      }

      // Check current state
      const hasVideo = files.some(f => f.type?.startsWith('video'));
      const imageCount = files.filter(f => f.type?.startsWith('image')).length;

      // If video exists, can't add more media
      if (hasVideo) {
        Alert.alert('Media Limit', 'Remove the video before selecting new media.');
        return;
      }

      // Check if we already have max images
      if (imageCount >= MAX_IMAGES) {
        Alert.alert('Limit Reached', `You can upload up to ${MAX_IMAGES} images per post.`);
        return;
      }

      // Calculate how many more images can be selected
      const remainingSlots = MAX_IMAGES - imageCount;

      const result = await launchImageLibrary({ 
        mediaType: 'mixed', // Allow both images and videos
        selectionLimit: remainingSlots,
        allowsMultipleSelection: true,
        quality: 0.8, // Compress images on native (0.8 = 80% quality)
      });

      if (result.didCancel) {
        return;
      }
      if (result.errorCode) {
        Alert.alert('Error', `Failed to select file: ${result.errorCode}`);
        return;
      }
      if (!result.assets || result.assets.length === 0) {
        return;
      }

      // Check if user selected a video
      const hasVideoSelected = result.assets.some((asset: Asset) => asset.type?.startsWith('video/'));
      const hasImageSelected = result.assets.some((asset: Asset) => asset.type?.startsWith('image/'));

      // Can't mix video and images
      if (hasVideoSelected && hasImageSelected) {
        Alert.alert('Media Error', 'Please select either images or a video, not both.');
        return;
      }

      // If video selected, only allow one video
      if (hasVideoSelected) {
        if (result.assets.length > 1) {
          Alert.alert('Media Limit', 'You can only select one video per post.');
          return;
        }
        const videoAsset = result.assets.find((asset: Asset) => asset.type?.startsWith('video/'));
        if (!videoAsset || !videoAsset.uri) {
          return;
        }

        // Compress video FIRST (on web), then validate compressed size
        // This allows larger videos to be compressed before applying the 50 MB limit
        const videoFileSize = (videoAsset as any).fileSize as number | undefined;
        let compressedUri = videoAsset.uri;
        let validatedSize: number | undefined = videoFileSize;
        let finalType = videoAsset.type || 'video/mp4';

        // Show compression indicator
        setGeneratingThumbnail(true);

        // Compress video:
        // - On web: actually compress using MediaRecorder (in compressionService)
        // - On native: currently returns original (no extra compression)
        try {
          const compressResult = await compressVideo(videoAsset.uri, {
            maxWidth: 1920,
            maxHeight: 1080,
            bitrate: 2000000, // 2 Mbps
          });

          if (compressResult.success && compressResult.data) {
            compressedUri = compressResult.data;

            // 1) WEB: always validate the COMPRESSED video against the standard MAX_FILE_SIZE (50 MB)
            if (Platform.OS === 'web') {
              // On web, compressed video is a data URL - calculate size from it
              if (compressedUri.startsWith('data:')) {
                const sizeValidation = await validateFileSize(compressedUri);
                if (!sizeValidation.isValid) {
                  setGeneratingThumbnail(false);
                  showFileSizeError(
                    sizeValidation.errorMessage ||
                      'Compressed video is still too large. Please select a smaller video.',
                  );
                  return;
                }
                validatedSize = sizeValidation.size;
              } else {
                // If for some reason it's not a data URL, fall back to validating the original
                const sizeValidation = await validateFileSize(videoAsset.uri, videoFileSize);
                if (!sizeValidation.isValid) {
                  setGeneratingThumbnail(false);
                  showFileSizeError(
                    sizeValidation.errorMessage || 'Video is too large. Please select a smaller video.',
                  );
                  return;
                }
                validatedSize = sizeValidation.size;
              }
            } else {
              // 2) NATIVE: keep the looser 200 MB limit (no extra compression yet)
              const MAX_VIDEO_SIZE_NATIVE = 200 * 1024 * 1024; // 200 MB
              const sizeValidation = await validateFileSize(videoAsset.uri, videoFileSize);
              
              if (sizeValidation.size > MAX_VIDEO_SIZE_NATIVE) {
                setGeneratingThumbnail(false);
                const sizeInMB = (sizeValidation.size / (1024 * 1024)).toFixed(2);
                showFileSizeError(`Video size (${sizeInMB} MB) exceeds the maximum allowed size of 200 MB. Please select a smaller video.`);
                return;
              }
              
              validatedSize = sizeValidation.size;
            }
          } else {
            // Compression reported failure: on web, treat this as an error for now
            if (Platform.OS === 'web') {
              setGeneratingThumbnail(false);
              Alert.alert(
                'Video',
                'Unable to compress this video. Please select a smaller or shorter video.',
              );
              return;
            } else {
              // Native: fall back to validating original with 200 MB limit
              const MAX_VIDEO_SIZE_NATIVE = 200 * 1024 * 1024;
              const sizeValidation = await validateFileSize(videoAsset.uri, videoFileSize);

              if (sizeValidation.size > MAX_VIDEO_SIZE_NATIVE) {
                setGeneratingThumbnail(false);
                const sizeInMB = (sizeValidation.size / (1024 * 1024)).toFixed(2);
                showFileSizeError(
                  `Video size (${sizeInMB} MB) exceeds the maximum allowed size of 200 MB. Please select a smaller video.`,
                );
                return;
              }

              validatedSize = sizeValidation.size;
            }
          }
        } catch (compressionError) {
          // If compression throws an error:
          if (Platform.OS === 'web') {
            setGeneratingThumbnail(false);
            Alert.alert(
              'Video',
              'Something went wrong while compressing the video. Please select a smaller or shorter video.',
            );
            return;
          } else {
            // Native: fall back to validating original with 200 MB limit
            const MAX_VIDEO_SIZE_NATIVE = 200 * 1024 * 1024;
            const sizeValidation = await validateFileSize(videoAsset.uri, videoFileSize);

            if (sizeValidation.size > MAX_VIDEO_SIZE_NATIVE) {
              setGeneratingThumbnail(false);
              const sizeInMB = (sizeValidation.size / (1024 * 1024)).toFixed(2);
              showFileSizeError(
                `Video size (${sizeInMB} MB) exceeds the maximum allowed size of 200 MB. Please select a smaller video.`,
              );
              return;
            }

            validatedSize = sizeValidation.size;
          }
        }

        // Extract extension
        let extension = 'mp4';
        if (finalType.startsWith('video/')) {
          extension = finalType.split('/')[1]; // e.g., 'mp4', 'mov'
        }
        
        // On web, compressed video might be WebM, update type accordingly
        if (Platform.OS === 'web' && compressedUri.startsWith('data:video/')) {
          const mimeMatch = compressedUri.match(/data:(video\/[^;]+)/);
          if (mimeMatch) {
            finalType = mimeMatch[1];
            extension = finalType.split('/')[1] || 'webm';
          }
        }

        const fileName = videoAsset.fileName || `video_${Date.now()}.${extension}`;
        
        // Set compressed video with loading state
        setFiles([{
          uri: compressedUri,
          type: finalType,
          fileName,
          thumbnailUri: undefined, // Will be set after generation
          fileSize: validatedSize,
        }]);
        
        // Generate thumbnail for preview (like Android app) - async
        // Use compressed URI if available, otherwise original
        try {
          const thumbGen = await generateVideoThumbnail(compressedUri || videoAsset.uri);
          
          if (thumbGen.success && thumbGen.path) {
            // Update files with thumbnail
            setFiles(prev => prev.map((f, idx) => 
              idx === 0 ? { ...f, thumbnailUri: thumbGen.path } : f
            ));
            // Set generatingThumbnail to false - React 18 will batch these updates
            setGeneratingThumbnail(false);
          } else {
            // Still show video, just without thumbnail
            setGeneratingThumbnail(false);
          }
        } catch (e) {
          // Continue without thumbnail - video will still work
          setGeneratingThumbnail(false);
        }
        return;
      }

      // Handle images
      const imageAssets = result.assets
        .filter((asset: Asset) => asset.uri && asset.type?.startsWith('image/'))
        .slice(0, remainingSlots);

      if (imageAssets.length === 0) {
        Alert.alert('Error', 'Please select image files');
        return;
      }

      const newFiles: FileAsset[] = [];

      for (const asset of imageAssets) {
        if (!asset.uri) continue;

        // Validate file size BEFORE proceeding
        // Pass fileSize if available (from File object on web)
        const fileSize = (asset as any).fileSize;
        if (fileSize === undefined || fileSize === null) {
          // Try to get size from Data URL if it's a data URL
          if (!asset.uri.startsWith('data:')) {
            Alert.alert('Error', 'Unable to determine image file size. Please try again.');
            continue;
          }
        }
        
        let validatedSize = fileSize;
        if (Platform.OS === 'web') {
          // On web, trust File.size and compare directly to MAX_FILE_SIZE
          if (typeof fileSize === 'number' && fileSize > 0) {
            if (fileSize > MAX_FILE_SIZE) {
              const sizeInMB = (fileSize / (1024 * 1024)).toFixed(2);
              const maxSizeInMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
              showFileSizeError(
                `File size (${sizeInMB} MB) exceeds the maximum allowed size of ${maxSizeInMB} MB. Please select a smaller image.`
              );
              continue; // Skip this image
            }
            validatedSize = fileSize;
          } else if (asset.uri.startsWith('data:')) {
            // Fallback: estimate from Data URL
            const sizeValidation = await validateFileSize(asset.uri);
            if (!sizeValidation.isValid) {
              showFileSizeError(sizeValidation.errorMessage || 'File size validation failed');
              continue;
            }
            validatedSize = sizeValidation.size;
          } else {
            Alert.alert('Error', 'Unable to determine image file size. Please try again.');
            continue;
          }
        } else {
          // Native: use validateFileSize (RNFS.stat)
          const sizeValidation = await validateFileSize(asset.uri, fileSize);
          if (!sizeValidation.isValid) {
            showFileSizeError(sizeValidation.errorMessage || 'File size validation failed');
            continue; // Skip this image but allow others
          }
          validatedSize = sizeValidation.size;
        }

        showFileSizeInfo(validatedSize);

      // Extract extension from MIME type
      let extension = 'jpg';
        const type = asset.type || 'image/jpeg';
      if (type.startsWith('image/')) {
        extension = type.split('/')[1]; // e.g., 'png', 'jpeg', 'webp'
        if (extension === 'jpeg') extension = 'jpg';
      }
        
        const fileName = asset.fileName || `image_${Date.now()}_${newFiles.length}.${extension}`;
        newFiles.push({
        uri: asset.uri,
        type,
        fileName,
        fileSize: validatedSize, // Store validated file size
      });
      }

      if (newFiles.length > 0) {
        setFiles(prev => [...prev, ...newFiles].slice(0, MAX_IMAGES));
      }
    } catch (e) {
      console.error('Error picking media:', e);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (!updated.some(file => file.type?.startsWith('video/'))) {
        setVideoPaused(true);
      }
      return updated;
    });
  };

  const hasVideoSelected = files.some(file => file.type?.startsWith('video/'));

  const onSubmit = async () => {
    try {
      // validate data
      if (!body && files.length === 0) {
        Alert.alert("Post", "Please choose media or add post body!");
        return;
      }

      // Validate: can't have both images and video
      const hasVideo = files.some(f => f.type?.startsWith('video'));
      const hasImages = files.some(f => f.type?.startsWith('image'));
      if (hasVideo && hasImages) {
        Alert.alert('Post', 'Please choose either images (up to 3) or a single video per post.');
        return;
      }

      setLoading(true);
      let data: any = {
        body: body,
        userId: userData?.id,
      };
      if (post && post.id) data.id = post.id;
      if (communityId) {
        data.community_id = communityId;
      }
      data.is_deal = isDeal;

      // Handle file upload
      if (files.length > 0) {
        const uploadedPaths: string[] = [];

        for (const file of files) {
          const isImage = file.type?.startsWith('image');
          const folder = isImage ? 'postImages' : 'postVideos';

          // Check if file is already uploaded (has http/https or is a storage path)
          if (file.uri.startsWith('http') || file.uri.includes('postImages') || file.uri.includes('postVideos')) {
            // Already uploaded, use the path directly
            uploadedPaths.push(file.uri);
            continue;
          }

          // If video: generate thumbnail from local URI
          if (!isImage && file.uri) {
            try {
              const thumbGen = await generateVideoThumbnail(file.uri);
              if (thumbGen.success && thumbGen.path) {
                const thumbUpload = await uploadFile('uploads', thumbGen.path, true, `thumb_${Date.now()}.jpg` || null, 'postImages');
                if (thumbUpload.success) {
                  (data as any).thumbnail = thumbUpload.data;
                }
              }
            } catch (e) {
              console.error('Video thumbnail generation failed:', e);
            }
          }

          // Upload the media
          const uploadResult = await uploadFile('uploads', file.uri, isImage, file.fileName || null, folder);
          if (uploadResult.success) {
            uploadedPaths.push(uploadResult.data);
          } else {
            Alert.alert('Post', uploadResult.msg || 'Failed to upload file');
            return;
          }
        }

        // Store as array if multiple images, single string if one image/video
        if (uploadedPaths.length === 1) {
          data.file = uploadedPaths[0];
        } else {
          data.file = uploadedPaths; // Array for multiple images
        }
      }

      console.log('[NewPost] Submitting post payload:', data);
      const res = await createOrUpdatePost(data);
      console.log('[NewPost] createOrUpdatePost result:', res);

      if (res.success && res.data) {
        setFiles([]);
        setBody("");
        setIsDeal(false);
        // Emit event with the new post data to add it immediately
        DeviceEventEmitter.emit('newPostCreated', res.data);
        // Navigate back
        navigation.goBack();
      } else {
        Alert.alert("Post", (res as any).msg || "Something went wrong");
      }
    } catch (error) {
      console.error('[NewPost] Error during submit:', error);
      Alert.alert('Post', 'Something went wrong while creating your post. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <ScreenWrapper bg="white">
      <View style={styles.container}>
        <Header title="Create Post" mb={15} />

        <ScrollView contentContainerStyle={{ gap: 20 }}>
          {/* header */}
          <View style={styles.header}>
            <Avatar
              uri={userData?.image || ""}
              size={hp(6.5)}
              rounded={theme.radius.xl}
            />
            <View style={{ gap: 2 }}>
              <Text style={styles.username}>{userData && userData.name}</Text>
              <Text style={styles.publicText}>Public</Text>
            </View>
          </View>

          <View style={styles.dealToggleRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.dealToggleTitle}>Mark as Deal</Text>
              <Text style={styles.dealToggleSubtitle}>
                Deals appear in the dedicated Deals section for members.
              </Text>
            </View>
            <Switch
              value={isDeal}
              onValueChange={setIsDeal}
              trackColor={{ false: theme.colors.gray, true: theme.colors.primary }}
              thumbColor="white"
            />
          </View>
          <View style={styles.textEditor}>
           <Input
              placeholder="What's on your mind?"
              placeholderTextColor={theme.colors.textLight}
              value={body}
              onChangeText={setBody}
              multiline={true}
            />
          </View>
          {communityId && (
            <Text style={{ color: theme.colors.primary, marginBottom: 8 }}>
              Posting in Community: <Text style={{ fontWeight: 'bold', color: theme.colors.primaryDark }}>{communityName || '...'}</Text>
            </Text>
          )}
          
          <View style={styles.mediaSection}>
            <Text style={styles.label}>
              Media (Optional) - Up to {MAX_IMAGES} images or 1 video
            </Text>
            {files.length > 0 && (
              <>
                {hasVideoSelected ? (
                  // Video preview with thumbnail (like Android app)
                  <View style={styles.videoPreviewContainer}>
                    {(() => {
                      // Debug logging
                      const debugInfo = {
                        generatingThumbnail,
                        hasThumbnail: !!files[0]?.thumbnailUri,
                        thumbnailUri: files[0]?.thumbnailUri?.substring(0, 50) + '...',
                        filesLength: files.length
                      };
                      console.log('[NewPost] Render video preview:', debugInfo);
                      
                      if (generatingThumbnail) {
                        // Show loading state while generating thumbnail
                        return (
                          <View style={styles.videoThumbnailLoading}>
                            <ActivityIndicator size="large" color={theme.colors.primary} />
                            <Text style={styles.videoThumbnailLoadingText}>Generating thumbnail...</Text>
                          </View>
                        );
                      } else if (files[0]?.thumbnailUri) {
                        // Show thumbnail image (first frame) like Android
                        return (
                          <RNImage
                            key={files[0].thumbnailUri.substring(0, 100)} // Force re-render if thumbnail changes
                            source={{ uri: files[0].thumbnailUri }}
                            style={styles.videoThumbnail}
                            resizeMode="cover"
                            onError={(e) => {
                              console.error('[NewPost] Thumbnail image load error:', e);
                            }}
                            onLoad={() => {
                              console.log('[NewPost] Thumbnail image loaded successfully');
                            }}
                          />
                        );
                      } else {
                        // Fallback: show a placeholder with video icon
                        return (
                          <View style={styles.videoThumbnailPlaceholder}>
                            <VideoIcon size={48} color={theme.colors.textLight} />
                            <Text style={styles.videoThumbnailPlaceholderText}>Video selected</Text>
                          </View>
                        );
                      }
                    })()}
                    {/* Play button overlay - only show if thumbnail is available */}
                    {files[0]?.thumbnailUri && !generatingThumbnail && (
                      <View style={styles.videoPlayOverlay}>
                        <Text style={styles.videoPlayIcon}>▶</Text>
                      </View>
                    )}
                    <TouchableOpacity
                      style={styles.removeMedia}
                      onPress={() => removeFile(0)}
                    >
                      <Text style={styles.removeMediaText}>×</Text>
                    </TouchableOpacity>
                  </View>
              ) : (
                  // Images grid
                  <View style={styles.imagesGrid}>
                    {files.map((file, index) => (
                      <View key={index} style={styles.imagePreviewContainer}>
                        <RNImage
                          source={{ uri: file.uri }}
                          style={styles.previewImage}
                          resizeMode="cover"
                        />
                        <TouchableOpacity
                          style={styles.removeMedia}
                          onPress={() => removeFile(index)}
                        >
                          <Text style={styles.removeMediaText}>×</Text>
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>
                )}
              </>
            )}
            {(!hasVideoSelected && files.length < MAX_IMAGES) && (
              <TouchableOpacity
                style={styles.mediaPicker}
                onPress={onPick}
                disabled={loading}
              >
                <View style={styles.mediaPlaceholder}>
                  {loading ? (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  ) : (
                    <>
                      <View style={styles.mediaIcons}>
                        <Image size={24} color={theme.colors.dark} />
                        <VideoIcon size={26} color={theme.colors.dark} />
                      </View>
                      <Text style={styles.mediaPlaceholderLabel}>
                        {files.length === 0 ? 'Add Images or Video' : `Add More Images (${MAX_IMAGES - files.length} left)`}
                      </Text>
                    </>
                  )}
                </View>
              </TouchableOpacity>
              )}
          </View>

          {/* Create Poll Button */}
          {!post && (
            <TouchableOpacity
              style={styles.pollButton}
              onPress={() => (navigation as any).navigate('CreatePoll', { communityId })}
            >
              <View style={styles.pollButtonContent}>
                <Text style={styles.pollButtonIcon}>📊</Text>
                <View style={styles.pollButtonText}>
                  <Text style={styles.pollButtonTitle}>Create Poll</Text>
                  <Text style={styles.pollButtonSubtitle}>Ask a question to the community</Text>
                </View>
                <Text style={styles.pollButtonArrow}>→</Text>
              </View>
            </TouchableOpacity>
          )}
        </ScrollView>
        <Button
          buttonStyle={{ height: hp(6.2) }}
          title={post && post.id ? "Update" : "Post"}
          loading={loading}
          hasShadow={false}
          onPress={onSubmit}
        />
      </View>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginBottom: 30,
    paddingHorizontal: wp(4),
    gap: 15,
  },
  title: {
    fontSize: hp(2.5),
    fontWeight: "600",
    color: theme.colors.text,
    textAlign: "center",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  username: {
    fontSize: hp(2),
    fontWeight: "600",
    color: theme.colors.text,
  },
  publicText: {
    fontSize: hp(1.4),
    color: theme.colors.textLight,
  },
  textEditor: {
    minHeight: hp(15),
  },
  mediaSection: {
    marginTop: 10,
    paddingVertical: 15,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.gray,
  },
  label: {
    fontSize: hp(1.8),
    fontWeight: "500",
    color: theme.colors.text,
    marginBottom: 10,
  },
  mediaPicker: {
    backgroundColor: theme.colors.gray,
    borderRadius: theme.radius.lg,
    overflow: "hidden",
    padding: 15,
  },
  mediaPreview: {
    position: "relative",
    width: "100%",
    height: 200,
    borderRadius: theme.radius.md,
    overflow: "hidden",
  },
  previewImage: {
    width: "100%",
    height: "100%",
    resizeMode: "cover",
  },
  videoContainer: {
    position: "relative",
    width: "100%",
    height: 200,
    borderRadius: theme.radius.md,
    overflow: "hidden",
  },
  videoPlayer: {
    width: "100%",
    height: "100%",
  },
  videoControl: {
    position: "absolute",
    top: 10,
    right: 10,
    backgroundColor: "rgba(0,0,0,0.5)",
    borderRadius: 15,
    padding: 5,
  },
  videoControlText: {
    color: "white",
    fontSize: 20,
  },
  mediaPlaceholder: {
    alignItems: "center",
    paddingVertical: 20,
  },
  mediaIcons: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 8,
  },
  mediaPlaceholderLabel: {
    fontSize: hp(1.8),
    fontWeight: "500",
    color: theme.colors.textLight,
    marginTop: 10,
  },
  removeMedia: {
    position: "absolute",
    top: 5,
    right: 5,
    backgroundColor: "rgba(255,0,0,0.6)",
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  removeMediaText: {
    color: "white",
    fontSize: 16,
    fontWeight: "bold",
  },
  imagesGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 10,
  },
  imagePreviewContainer: {
    position: "relative",
    width: (wp(100) - wp(8) - 20) / 3, // 3 columns with gaps
    height: (wp(100) - wp(8) - 20) / 3,
    borderRadius: theme.radius.md,
    overflow: "hidden",
  },
  videoPreviewContainer: {
    position: "relative",
    width: "100%",
    height: 200,
    borderRadius: theme.radius.md,
    overflow: "hidden",
    marginBottom: 10,
  },
  videoThumbnail: {
    width: "100%",
    height: "100%",
    resizeMode: "cover",
  },
  videoPlayOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: [{ translateX: -25 }, { translateY: -25 }],
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
  },
  videoPlayIcon: {
    color: "white",
    fontSize: 20,
    marginLeft: 3, // Slight offset for visual centering
  },
  videoThumbnailLoading: {
    width: "100%",
    height: "100%",
    backgroundColor: theme.colors.gray,
    justifyContent: "center",
    alignItems: "center",
  },
  videoThumbnailLoadingText: {
    marginTop: 10,
    fontSize: hp(1.6),
    color: theme.colors.textLight,
  },
  videoThumbnailPlaceholder: {
    width: "100%",
    height: "100%",
    backgroundColor: theme.colors.gray,
    justifyContent: "center",
    alignItems: "center",
  },
  videoThumbnailPlaceholderText: {
    marginTop: 10,
    fontSize: hp(1.6),
    color: theme.colors.textLight,
  },
  dealToggleRow: {
    marginTop: 10,
    padding: 15,
    borderWidth: 1,
    borderColor: theme.colors.gray,
    borderRadius: theme.radius.lg,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  dealToggleTitle: {
    fontSize: hp(1.8),
    fontWeight: "600",
    color: theme.colors.text,
    marginBottom: 4,
  },
  dealToggleSubtitle: {
    fontSize: hp(1.35),
    color: theme.colors.textLight,
  },
  pollButton: {
    backgroundColor: theme.colors.gray,
    borderRadius: theme.radius.lg,
    padding: 16,
    marginTop: 10,
  },
  pollButtonContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  pollButtonIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  pollButtonText: {
    flex: 1,
  },
  pollButtonTitle: {
    fontSize: hp(1.8),
    fontWeight: "600",
    color: theme.colors.text,
    marginBottom: 2,
  },
  pollButtonSubtitle: {
    fontSize: hp(1.4),
    color: theme.colors.textLight,
  },
  pollButtonArrow: {
    fontSize: 20,
    color: theme.colors.primary,
    fontWeight: "bold",
  },
});

export default NewPost;
